import { useEffect, useState } from "react";
import { Badge, Card, Flex, Tooltip, Typography } from "antd";
import { BarChartOutlined, InfoCircleOutlined, UnorderedListOutlined } from "@ant-design/icons";
import { useGo } from "@refinedev/core";
import { useSelectedStores } from "@/config/context/SelectedItemsContext";
import { BoxCloseIcon } from "@/components/icon";
import { formatAmount, type DashboardPeriod } from "../../utils";
import { estadoDeWidget, WidgetPlaceholder } from "../widget-state";
import type { WidgetDataProps } from "../../types";
import dayjs from "dayjs";
import { ORDER_STATUS_GROUPS } from "@/utilities";
import {
  AreaChart, Area, XAxis, YAxis, ResponsiveContainer, Tooltip as RechartsTooltip,
} from "recharts";

const { Text } = Typography;

type StatRow = { count: number; amount: number; loading: boolean };
type GroupKey = "open" | "complete" | "canceled";
type ChartPoint = { date: string; open: number; complete: number; canceled: number };

// Mismo agrupamiento que `ORDER_GROUP_SQL` en xcon-api: si se separan, la fila lleva a un listado
// que no reproduce su número. Vive en un solo lugar para que el listado use el mismo.
const STATUS_GROUPS = ORDER_STATUS_GROUPS;

const STATE_COLORS: Record<GroupKey, string> = {
  open:     "#faad14",
  complete: "#52c41a",
  canceled: "#ff4d4f",
};

const STATE_LABELS: Record<GroupKey, string> = {
  open: "Abierto", complete: "Completo", canceled: "Cancelado",
};

const GROUPS: GroupKey[] = ["open", "complete", "canceled"];



function formatDateKey(key: string, period: DashboardPeriod): string {
  if (period === "week" || period === "month" || period === "day")
    return dayjs(key).format("D/M");
  if (period === "year") return dayjs(key + "-01").format("MMM");
  return dayjs(key + "-01").format("MMM YY");
}

type Datos = {
  estados: { state: string; count: number; amount: number }[];
  serie?: { date: string; state: string; count: number }[];
  total: { count: number; amount: number };
};

export const PedidosChips = ({
  period,
  bare = false,
  height,
  data,
  estado,
  error,
  nuncaCalculado,
  recargar,
}: {
  period: DashboardPeriod;
  bare?: boolean;
  height?: number;
} & WidgetDataProps<Datos>) => {
  const { selectedStores } = useSelectedStores();
  const go = useGo();
  const [view, setView] = useState<"list" | "chart">("list");
  const sellerCodes = selectedStores.map((s) => s.code);

  const cargando = estado === "cargando";
  const porGrupo = new Map((data?.estados ?? []).map((e) => [e.state, e]));
  const stats = Object.fromEntries(
    GROUPS.map((g) => [
      g,
      {
        count: porGrupo.get(g)?.count ?? 0,
        amount: porGrupo.get(g)?.amount ?? 0,
        loading: cargando,
      },
    ]),
  ) as Record<GroupKey, StatRow>;

  // La serie del gráfico llega por día y por grupo; se pivotea a un punto por fecha.
  const porFecha = new Map<string, ChartPoint>();
  for (const p of data?.serie ?? []) {
    const key = formatDateKey(p.date, period);
    const punto = porFecha.get(key) ?? { date: key, open: 0, complete: 0, canceled: 0 };
    if (p.state === "open" || p.state === "complete" || p.state === "canceled") {
      punto[p.state] += p.count;
    }
    porFecha.set(key, punto);
  }
  const chartData: ChartPoint[] = [...porFecha.values()];
  const loadingChart = cargando;
  const status = estadoDeWidget(
    estado,
    error,
    nuncaCalculado,
    (data?.total?.count ?? 0) > 0,
    recargar,
  );

  /** El título de la sección es un atajo al listado, con el filtro de store (QA #273.7). */
  const irAlListado = () => {
    const filters: any[] = [];
    if (sellerCodes.length > 0) {
      filters.push({ field: "sellerCode", operator: "in", value: sellerCodes });
    }
    go({ to: "/orders", type: "push", query: { filters } });
  };

  const handleRowClick = (group: GroupKey) => {
    const filters: any[] = [
      { field: "status", operator: "in", value: [...STATUS_GROUPS[group]] },
    ];
    if (sellerCodes.length > 0) {
      filters.push({ field: "sellerCode", operator: "in", value: sellerCodes });
    }
    go({ to: "/orders", type: "push", query: { filters } });
  };

  const totalCount = Object.values(stats).reduce((s, r) => s + r.count, 0);
  const totalAmount = Object.values(stats).reduce((s, r) => s + r.amount, 0);
  const anyLoading = Object.values(stats).some((r) => r.loading);

  const rows: { key: GroupKey; badgeStatus: "warning" | "success" | "error"; label: string }[] = [
    { key: "open",     badgeStatus: "warning", label: "Abierto" },
    { key: "complete", badgeStatus: "success", label: "Completo" },
    { key: "canceled", badgeStatus: "error",   label: "Cancelado" },
  ];

  const CONTENT_H = Math.max(60, (height ?? 180) - 66); // 40px card header + 26px toggle footer

  const rowItems = rows.map((row) => {
    const stat = stats[row.key];
    return (
      <Flex key={row.key} align="center" justify="space-between"
        style={{ padding: bare ? "8px 16px" : "6px 16px", cursor: "pointer" }}
        onClick={() => handleRowClick(row.key)}
        onMouseEnter={(e) => (e.currentTarget.style.background = "#fafafa")}
        onMouseLeave={(e) => (e.currentTarget.style.background = "transparent")}
      >
        <Flex align="center" gap={8}>
          <Badge status={row.badgeStatus} />
          <Text strong style={{ fontSize: 13 }}>{row.label}</Text>
        </Flex>
        <Flex align="center" gap={10}>
          <Text type="secondary" style={{ fontSize: 13, opacity: stat.loading ? 0.4 : 1 }}>
            {stat.count}
          </Text>
          <Text strong style={{ fontSize: 15, lineHeight: 1, opacity: stat.loading ? 0.4 : 1, minWidth: 48, textAlign: "right" }}>
            {formatAmount(stat.amount)}
          </Text>
        </Flex>
      </Flex>
    );
  });

  const chartView = (
    <div style={{ height: CONTENT_H, opacity: loadingChart ? 0.4 : 1 }}>
      {!loadingChart && chartData.length === 0 ? (
        <Flex align="center" justify="center" style={{ height: "100%" }}>
          <Text type="secondary" style={{ fontSize: 12 }}>Sin datos para el período</Text>
        </Flex>
      ) : (
        <ResponsiveContainer width="100%" height={CONTENT_H}>
          <AreaChart data={chartData} margin={{ top: 4, right: 8, left: -28, bottom: 0 }}>
            <XAxis dataKey="date" tick={{ fontSize: 10 }} tickLine={false} axisLine={false} />
            <YAxis tick={{ fontSize: 10 }} tickLine={false} axisLine={false} allowDecimals={false} />
            <RechartsTooltip
              contentStyle={{ fontSize: 12, borderRadius: 6, background: "#fff", border: "none", boxShadow: "0 2px 8px rgba(0,0,0,0.1)", color: "rgba(0,0,0,0.85)" }}
              formatter={(value: any, name: any) => [value, STATE_LABELS[name as GroupKey] ?? name]}
            />
            {GROUPS.map((group) => (
              <Area
                key={group}
                type="monotone"
                dataKey={group}
                stroke={STATE_COLORS[group]}
                fill={STATE_COLORS[group]}
                fillOpacity={0.15}
                strokeWidth={1.5}
                dot={false}
                name={group}
              />
            ))}
          </AreaChart>
        </ResponsiveContainer>
      )}
    </div>
  );

  const periodLabels: Record<string, string> = {
    day: "Hoy", week: "Esta semana", prev_week: "Semana anterior", month: "Mes en curso", prev_month: "Mes anterior", year: "Este año", historic: "Histórico",
  };

  const viewToggle = (
    <Flex justify="space-between" align="center" style={{ borderTop: "1px solid #f0f0f0", padding: "4px 12px" }}>
      <Flex gap={0} align="center">
        <button
          onClick={() => setView("list")}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 6px", color: view === "list" ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0.25)", display: "inline-flex", alignItems: "center" }}
        >
          <UnorderedListOutlined style={{ fontSize: 11 }} />
        </button>
        <span style={{ color: "rgba(0,0,0,0.1)" }}>|</span>
        <button
          onClick={() => setView("chart")}
          style={{ background: "none", border: "none", cursor: "pointer", padding: "2px 6px", color: view === "chart" ? "rgba(0,0,0,0.75)" : "rgba(0,0,0,0.25)", display: "inline-flex", alignItems: "center" }}
        >
          <BarChartOutlined style={{ fontSize: 11 }} />
        </button>
      </Flex>
      <Text type="secondary" style={{ fontSize: 12, opacity: anyLoading ? 0.4 : 1 }}>
        {totalCount} × {formatAmount(totalAmount)}
      </Text>
    </Flex>
  );

  if (bare) {
    return (
      <div>
        <Flex align="center" justify="space-between"
          style={{
            background: "rgba(0,0,0,0.02)",
            borderBottom: "1px solid #f0f0f0",
            padding: "0 16px",
            minHeight: 40,
          }}
        >
          <Flex align="center" gap={8}>
            <BoxCloseIcon style={{ width: 16, height: 16, color: "#15744b" }} />
            <span
              style={{ color: "#15744b", fontWeight: 600, cursor: "pointer" }}
              onClick={irAlListado}
            >
              Pedidos
            </span>
            <Tooltip
              title="Pedidos agrupados por estado — en curso, completados o cancelados — con su cantidad y monto total. Tocá una fila para explorar el detalle."
              trigger="click"
              color="#fff"
              overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
              overlayStyle={{ maxWidth: 240 }}
            >
              <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
            </Tooltip>
          </Flex>
          <Text type="secondary" style={{ fontSize: 13, opacity: anyLoading ? 0.4 : 1 }}>
            {totalCount} x {formatAmount(totalAmount)}
          </Text>
        </Flex>
        {rowItems}
      </div>
    );
  }

  return (
    <Card
      size="small"
      style={{ height: height ?? 180 }}
      styles={{ body: { padding: 0 } }}
      title={
        <Flex align="center" gap={8}>
          <BoxCloseIcon style={{ width: 16, height: 16, color: "#15744b" }} />
          <span
            style={{ color: "#15744b", fontWeight: 600, cursor: "pointer" }}
            onClick={irAlListado}
          >
            Pedidos
          </span>
          <Tooltip
            title="Pedidos agrupados por estado — en curso, completados o cancelados — con su cantidad y monto total. Tocá una fila para explorar el detalle."
            trigger="click"
            color="#fff"
            overlayInnerStyle={{ color: "rgba(0,0,0,0.85)", boxShadow: "0 2px 8px rgba(0,0,0,0.1)" }}
            overlayStyle={{ maxWidth: 240 }}
          >
            <InfoCircleOutlined style={{ fontSize: 12, color: "rgba(0,0,0,0.3)", cursor: "pointer" }} onClick={(e) => e.stopPropagation()} />
          </Tooltip>
        </Flex>
      }
    >
      <div style={{ height: CONTENT_H, overflow: "hidden", display: "flex", flexDirection: "column", justifyContent: view === "list" ? "center" : "flex-start" }}>
        {status.tipo !== "ok" ? <WidgetPlaceholder status={status} /> : view === "list" ? rowItems : chartView}
      </div>
      {viewToggle}
    </Card>
  );
};
